import { apiHandler } from "../buyerApiHandler";

/**
 * Banner Cards Studio: look and motion of the three organisation banner boxes
 * under the search bar on the buyer home (Row, Carousel or Train).
 * Buyer API /admin/banner-cards (x-api-key, sent by apiHandler).
 *
 * Types and ranges mirror ONDC-Buyer-API/src/banner-cards/banner-cards-style.ts.
 * The server sanitises everything (clamps numbers, drops bad colours / URLs),
 * so keep BANNER_RANGES in step with sanitizeBannerCardsStyle().
 */

export const BANNER_MODES = ["row", "carousel", "train"] as const;
export const TITLE_POSITIONS = ["top", "center", "bottom"] as const;
export const ENGINE_PRESETS = ["steam", "bullet", "toy", "festival"] as const;
export const TRAIN_MOTIONS = ["continuous", "stop"] as const;

export type BannerMode = (typeof BANNER_MODES)[number];
export type TitlePosition = (typeof TITLE_POSITIONS)[number];
export type EnginePreset = (typeof ENGINE_PRESETS)[number];
export type TrainMotion = (typeof TRAIN_MOTIONS)[number];
export type BoxShape = { points: Array<[number, number]>; roundness: number };

export interface BannerCardsStyle {
  mode: BannerMode;
  box: {
    /** Polygon (0..1); null = rounded rectangle with `radius`. */
    shape: BoxShape | null;
    radius: number;
    height: number;
    /** Width = height × ratio (row mode fills the width instead). */
    widthRatio: number;
    gap: number;
    borderColor: string;
    borderWidth: number;
    shadow: boolean;
  };
  title: {
    show: boolean;
    position: TitlePosition;
    size: number;
    color: string;
    fade: boolean;
  };
  carousel: { intervalSec: number };
  train: {
    engine: { preset: EnginePreset; artUrl: string | null; color: string; accent: string };
    coach: { color: string; color2: string; stripe: string; frame: string };
    wheels: { show: boolean; color: string };
    motion: TrainMotion;
    speed: number;
    direction: "left" | "right";
    stopSec: number;
    gapSec: number;
    smoke: boolean;
    bounce: boolean;
    track: { show: boolean; color: string };
    soundUrl: string | null;
    haptic: boolean;
  };
}

export const DEFAULT_BANNER_CARDS_STYLE: BannerCardsStyle = {
  mode: "row",
  box: {
    shape: null,
    radius: 16,
    height: 76,
    widthRatio: 1.35,
    gap: 8,
    borderColor: "rgba(255,255,255,0.85)",
    borderWidth: 2,
    shadow: false,
  },
  title: { show: true, position: "top", size: 11, color: "#FFFFFF", fade: true },
  carousel: { intervalSec: 3 },
  train: {
    engine: { preset: "steam", artUrl: null, color: "#C62828", accent: "#FFD54A" },
    coach: { color: "#1565C0", color2: "#0D47A1", stripe: "#FFD54A", frame: "#FFFFFF" },
    wheels: { show: true, color: "#263238" },
    motion: "stop",
    speed: 70,
    direction: "left",
    stopSec: 5,
    gapSec: 2,
    smoke: true,
    bounce: true,
    track: { show: true, color: "rgba(255,255,255,0.75)" },
    soundUrl: null,
    haptic: true,
  },
};

/** Slider ranges, identical to the server's clamps. */
export const BANNER_RANGES = {
  radius: { min: 0, max: 40, step: 1 },
  height: { min: 56, max: 140, step: 1 },
  widthRatio: { min: 0.6, max: 2, step: 0.05 },
  gap: { min: 0, max: 24, step: 1 },
  borderWidth: { min: 0, max: 5, step: 0.5 },
  titleSize: { min: 9, max: 16, step: 1 },
  intervalSec: { min: 2, max: 10, step: 0.5 },
  speed: { min: 20, max: 220, step: 5 },
  stopSec: { min: 1, max: 20, step: 0.5 },
  gapSec: { min: 0, max: 30, step: 0.5 },
  roundness: { min: 0, max: 0.5, step: 0.01 },
} as const;

const oneOf = <T extends readonly string[]>(v: unknown, list: T, fb: T[number]): T[number] =>
  (list as readonly string[]).includes(v as string) ? (v as T[number]) : fb;
const str = (v: unknown, fb: string) => (typeof v === "string" && v.trim() ? v : fb);
const numOr = (v: unknown, fb: number) => (typeof v === "number" && Number.isFinite(v) ? v : fb);
const boolOr = (v: unknown, fb: boolean) => (typeof v === "boolean" ? v : fb);
const urlOr = (v: unknown) => (typeof v === "string" && v.trim() ? v : null);

const shapeOr = (raw: any): BoxShape | null => {
  const pts = raw?.points;
  if (!Array.isArray(pts) || pts.length < 3) return null;
  return {
    points: pts.map((p: any) => [Number(p?.[0]) || 0, Number(p?.[1]) || 0] as [number, number]),
    roundness: numOr(raw?.roundness, 0.2),
  };
};

/**
 * Fill gaps so the editor and preview always have every field. Builds the
 * object in a fixed key order so draft-vs-saved comparison is stable.
 */
export const styleWithDefaults = (raw: any): BannerCardsStyle => {
  const r = raw && typeof raw === "object" ? raw : {};
  const d = DEFAULT_BANNER_CARDS_STYLE;
  const b = r.box ?? {};
  const t = r.title ?? {};
  const tr = r.train ?? {};
  const e = tr.engine ?? {};
  const c = tr.coach ?? {};
  return {
    mode: oneOf(r.mode, BANNER_MODES, d.mode),
    box: {
      shape: shapeOr(b.shape),
      radius: numOr(b.radius, d.box.radius),
      height: numOr(b.height, d.box.height),
      widthRatio: numOr(b.widthRatio, d.box.widthRatio),
      gap: numOr(b.gap, d.box.gap),
      borderColor: str(b.borderColor, d.box.borderColor),
      borderWidth: numOr(b.borderWidth, d.box.borderWidth),
      shadow: boolOr(b.shadow, d.box.shadow),
    },
    title: {
      show: boolOr(t.show, d.title.show),
      position: oneOf(t.position, TITLE_POSITIONS, d.title.position),
      size: numOr(t.size, d.title.size),
      color: str(t.color, d.title.color),
      fade: boolOr(t.fade, d.title.fade),
    },
    carousel: { intervalSec: numOr(r.carousel?.intervalSec, d.carousel.intervalSec) },
    train: {
      engine: {
        preset: oneOf(e.preset, ENGINE_PRESETS, d.train.engine.preset),
        artUrl: urlOr(e.artUrl),
        color: str(e.color, d.train.engine.color),
        accent: str(e.accent, d.train.engine.accent),
      },
      coach: {
        color: str(c.color, d.train.coach.color),
        color2: str(c.color2, d.train.coach.color2),
        stripe: str(c.stripe, d.train.coach.stripe),
        frame: str(c.frame, d.train.coach.frame),
      },
      wheels: {
        show: boolOr(tr.wheels?.show, d.train.wheels.show),
        color: str(tr.wheels?.color, d.train.wheels.color),
      },
      motion: oneOf(tr.motion, TRAIN_MOTIONS, d.train.motion),
      speed: numOr(tr.speed, d.train.speed),
      direction: tr.direction === "right" ? "right" : "left",
      stopSec: numOr(tr.stopSec, d.train.stopSec),
      gapSec: numOr(tr.gapSec, d.train.gapSec),
      smoke: boolOr(tr.smoke, d.train.smoke),
      bounce: boolOr(tr.bounce, d.train.bounce),
      track: {
        show: boolOr(tr.track?.show, d.train.track.show),
        color: str(tr.track?.color, d.train.track.color),
      },
      soundUrl: urlOr(tr.soundUrl),
      haptic: boolOr(tr.haptic, d.train.haptic),
    },
  };
};

/** One banner box as the preview draws it. */
export type PreviewBanner = {
  id: string | number;
  title: string;
  imageUrl: string | null;
  /** Placeholder fill when there is no photo. */
  gradient: [string, string];
};

export const SAMPLE_BANNERS: PreviewBanner[] = [
  { id: "s1", title: "Tazty", imageUrl: null, gradient: ["#FF8A3D", "#E8451E"] },
  { id: "s2", title: "No Platform Fee", imageUrl: null, gradient: ["#7C4DFF", "#3D1FB3"] },
  { id: "s3", title: "Fastest Delivery", imageUrl: null, gradient: ["#00BFA5", "#00796B"] },
];

const json = { headers: { "Content-Type": "application/json" } };

// The Buyer API wraps bodies in { success, data }.
const unwrap = (res: any) => res?.data ?? res;

export const getBannerCardsStyle = async (): Promise<{ style: BannerCardsStyle; defaults: BannerCardsStyle }> => {
  const body = unwrap(await apiHandler("get", "/admin/banner-cards/style"));
  return {
    style: styleWithDefaults(body?.style),
    defaults: styleWithDefaults(body?.defaults ?? DEFAULT_BANNER_CARDS_STYLE),
  };
};

export const saveBannerCardsStyle = async (style: BannerCardsStyle): Promise<BannerCardsStyle> =>
  styleWithDefaults(unwrap(await apiHandler("patch", "/admin/banner-cards/style", style, json)));

const VIDEO_EXT = /\.(mp4|mov|webm|m3u8|mkv)(\?|#|$)/i;

/**
 * The organisation banners (promotion_type "organization") in app order, as
 * preview boxes. Videos use their poster frame. Empty when there are none.
 */
export const getOrgBannersForPreview = async (): Promise<PreviewBanner[]> => {
  const body = await apiHandler("get", "/banner", undefined, { params: { limit: 100 } });
  // Shapes seen: { data: { data: [...] } }, { data: [...] } or a bare array.
  const inner = unwrap(body);
  const list: any[] = Array.isArray(inner) ? inner : Array.isArray(inner?.data) ? inner.data : [];
  return list
    .filter((b) => b?.promotion_type === "organization" && b?.status !== false)
    .sort((a, b) => (Number(a?.sequence) || 0) - (Number(b?.sequence) || 0))
    .map((b, i) => {
      const candidates = [b?.poster_url, b?.image_url].filter(
        (u): u is string => typeof u === "string" && /^https?:\/\//i.test(u) && !VIDEO_EXT.test(u),
      );
      return {
        id: b?.id ?? i,
        title: typeof b?.title === "string" ? b.title : "",
        imageUrl: candidates[0] ?? null,
        gradient: SAMPLE_BANNERS[i % SAMPLE_BANNERS.length].gradient,
      };
    });
};
